import { useState, useRef, useEffect } from "react";
import { Globe } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/hooks/useTranslation";
import { useAppContext } from "@/App";
import api from "@/lib/api";

export default function LanguageSwitcher({ compact = false }) {
  const { auth, setAuth } = useAppContext();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const currentLang = localStorage.getItem("pigeon-lang") || "en";
  const current = SUPPORTED_LANGUAGES.find(l => l.code === currentLang) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = async (code) => {
    localStorage.setItem("pigeon-lang", code);
    document.documentElement.lang = code;
    document.documentElement.dir = code === "ar" ? "rtl" : "ltr";

    // Persist to DB if logged in
    if (auth?.jwt) {
      try {
        await api.updateLanguage(auth.jwt, code);
        setAuth(prev => ({ ...prev, user: { ...prev.user, language: code } }));
      } catch (e) {
        console.error("Language save error:", e);
      }
    }

    setOpen(false);
    window.location.reload();
  };

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-xl transition-all ${
          compact
            ? "p-2 bg-slate-100 hover:bg-slate-200 text-slate-500"
            : "px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-500 text-xs font-bold"
        }`}
        data-testid="language-switcher-btn"
      >
        <Globe className="w-3.5 h-3.5" />
        {!compact && <span className="uppercase">{current.flag}</span>}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg z-[999] overflow-hidden animate-fadeIn" data-testid="language-dropdown">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`w-full px-3 py-2.5 text-left text-xs font-medium flex items-center justify-between hover:bg-slate-50 transition-colors ${
                currentLang === lang.code ? "bg-lime-50 text-lime-600 font-bold" : "text-slate-600"
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span>{lang.label}</span>
              <span className="text-[10px] font-bold text-slate-400 uppercase">{lang.flag}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
